import { Clear } from "@mui/icons-material";
import {
    Alert,
    Autocomplete,
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormLabel,
    Grid,
    IconButton,
    LinearProgress,
    List,
    ListItem,
    ListItemText,
    Paper,
    Radio,
    RadioGroup,
    Slider,
    TextField,
    ToggleButton,
    Tooltip,
    Typography
} from "@mui/material";
import { sanitizeFormLabelHtml } from "../../_helpers/sanitizeFormLabelHtml";
import { useEffect, useRef, useState } from "react";
import { ValidatorForm } from "react-material-ui-form-validator";
import { useNavigate } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import { FormFieldKeyConditionEnum } from "../../_common/enum/forms/formFieldKeyCondition.enum";
import { FormFieldKeyActionEnum } from "../../_common/enum/forms/formFieldKeyAction.enum";
import { FormFieldKeyConditionTypeEnum } from "../../_common/enum/forms/formFieldKeyConditionType.enum";
import { FormFieldKeySpecialTypeEnum } from "../../_common/enum/forms/formFieldKeySpecialType.enum";
import { FormFieldValueTypeEnum } from "../../_common/enum/forms/formFieldValueType.enum";
import { FormStatusEnum } from "../../_common/enum/forms/formStatus.enum";
import { IFormFieldKeyManage } from "../../_common/models/formFieldKey";
import { IFormFieldKeyLookupManage } from "../../_common/models/formFieldKeyLookup";
import { IFormTemplateSectionFormManage } from "../../_common/models/formTemplateSection/formTemplateSectionFormManage.model";
import { DataTypeAutoComplete } from "../../_components/autocomplete/dataType.autoComplete";
import { PrimaryButton } from "../../_components/button/primary.button";
import { StandardTextValidator } from "../../_components/textValidator/standardTextValidator";
import { API_URL } from "../../_config/config.const";
import { parseDDMMYYYYToISO } from "../../_helpers/dateTime.helper";
import { applyValidationRules, removeValidationRules } from "../../_helpers/formValidation.helper";
import { handleErrorResponse, handleErrorResponseAxios } from "../../_helpers/handleResponse.helper";
import { accountService } from "../../_services/accounts.service";
import { formService } from "../../_services/form.service";
import { formSectionService } from "../../_services/formSection.service";
import { formTemplateSectionService } from "../../_services/formTemplateSection.service";
import { tokenService } from "../../_services/token.service";

/**
 * Renders one template section. Stepper visibility (disabled vs hidden when conditions fail) is handled in FormOriginalManage.
 */
export function FormSectionOriginalManage(params: {
    formId?: number;
    formTemplateSectionId: number,
    formSectionId?: number,
    isSectionSaving?: boolean,
    isForReview?: boolean,
    actionId?: FormFieldKeyActionEnum,
    onSave: () => void,
    isLastSection?: boolean
}) {
    const signatureRefs = useRef<Record<number, any>>({});
    const loadedSignatureValuesRef = useRef<Record<number, string>>({});

    const [data, setData] = useState<IFormTemplateSectionFormManage>();
    const [isLoading, setLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const _formId = params.formId ?? Number(params.formId ?? 0);

    const navigate = useNavigate();

    useEffect(() => {
        fetchTemplateSectionData();
    }, [params.formTemplateSectionId]);

    useEffect(() => {
        applyValidationRules();
        return () => {
            removeValidationRules();
        };
    }, []);

    useEffect(() => {
        if (!data?.formFieldKeys?.length || params.isForReview) return;

        const timer = setTimeout(() => {
            data && data.formFieldKeys && data.formFieldKeys
                .filter((ffk) => ffk.formFieldValueTypeId === FormFieldValueTypeEnum.Signature)
                .forEach((ffk) => {
                    const sig = signatureRefs.current[ffk.id];
                    const value = ffk.formField?.fieldValue ?? "";

                    if (!sig) return;
                    if (loadedSignatureValuesRef.current[ffk.id] === value) return;

                    try {
                        sig.clear();

                        if (value) {
                            sig.fromDataURL(value);
                        }

                        loadedSignatureValuesRef.current[ffk.id] = value;
                    } catch (err) {
                        console.error("Failed to load signature for field", ffk.id, err);
                    }
                });
        }, 0);

        return () => clearTimeout(timer);
    }, [params.formTemplateSectionId, params.isForReview, data?.formFieldKeys]);

    function safeString(value: any) {
        return value == null ? "" : String(value);
    }

    function safeLower(value: any) {
        return safeString(value).toLowerCase();
    }

    function getSignatureRef(fieldId: number) {
        if (!signatureRefs.current[fieldId]) {
            signatureRefs.current[fieldId] = null;
        }

        return (ref: any) => {
            signatureRefs.current[fieldId] = ref;
        };
    }

    function fetchTemplateSectionData() {
        if (params.formTemplateSectionId == 0) return;
        setLoading(true);

        return formTemplateSectionService
            .getForm(params.formTemplateSectionId, _formId, params.isForReview, params.actionId)
            .then((json) => {
                const updated = {
                    ...json,
                    formFieldKeys: json.formFieldKeys?.map((ffk: any) => {
                        if (
                            (ffk.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.DateOfBirth ||
                                ffk.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.DateOfBirthJointLife) &&
                            ffk.formField?.fieldValue
                        ) {
                            const dateValue = ffk.formField.fieldValue as string;

                            if (!/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
                                return {
                                    ...ffk,
                                    formField: {
                                        ...ffk.formField,
                                        fieldValue: parseDDMMYYYYToISO(dateValue),
                                    },
                                };
                            }
                        }
                        return ffk;
                    }) ?? []
                };

                setData(updated);
            })
            .finally(() => {
                setLoading(false);
            });
    }

    function handleChange(event: any, ffkId: number) {
        setData((_data: any) => {
            const __data = { ..._data };

            if (!__data.formFieldKeys?.length) {
                return __data as IFormTemplateSectionFormManage;
            }

            const _formFieldKey = __data.formFieldKeys.find(
                (ffk: IFormFieldKeyManage) => ffk.id === ffkId
            );

            if (!_formFieldKey) {
                return __data as IFormTemplateSectionFormManage;
            }

            const isCheckbox =
                _formFieldKey.formFieldValueTypeId === FormFieldValueTypeEnum.Checkbox ||
                _formFieldKey.formFieldValueTypeId === FormFieldValueTypeEnum.Consent;
            const isPhone = _formFieldKey.formFieldValueTypeId === FormFieldValueTypeEnum.PhoneNumber;
            const value = isCheckbox ? (event?.target?.checked ? "Yes" : "No") : (event?.target?.value ?? "");

            if (isPhone) {
                const phoneRegex = /^[0-9+\-()\s]*$/;
                if (!phoneRegex.test(value)) {
                    return __data as IFormTemplateSectionFormManage;
                }

                const digitCount = value.replace(/\D/g, "").length;
                if (digitCount > 15) {
                    return __data as IFormTemplateSectionFormManage;
                }
            }

            if (_formFieldKey.formField) {
                _formFieldKey.formField.fieldValue = value;
            } else {
                _formFieldKey.formField = {
                    id: 0,
                    fieldValue: value,
                    formFieldKeyId: _formFieldKey.id,
                    formTemplateSectionId: _formFieldKey.formTemplateSectionId,
                    formSectionId: params.formSectionId ?? 0
                };
            }

            return { ...__data } as IFormTemplateSectionFormManage;
        });
    }

    function handleChangeDataType(event: any, ffkId: number) {
        setData((_data: any) => {
            const __data = { ..._data };

            if (__data.formFieldKeys && __data.formFieldKeys.length) {
                const _formFieldKey = __data.formFieldKeys.find((ffk: IFormFieldKeyManage) => ffk.id === ffkId);

                if (!_formFieldKey) {
                    return __data as IFormTemplateSectionFormManage;
                }

                if (_formFieldKey.formField) {
                    _formFieldKey.formField.fieldValue = event.target.value;
                } else {
                    _formFieldKey.formField = {
                        id: 0,
                        fieldValue: event.target.value,
                        formFieldKeyId: _formFieldKey.id,
                        formTemplateSectionId: _formFieldKey.formTemplateSectionId,
                        formSectionId: params.formSectionId ?? 0
                    };
                }
            }

            return { ...__data } as IFormTemplateSectionFormManage;
        });
    }

    function handleAutocompleteChange(event: any, ffkId: any, value: any) {
        event.preventDefault();

        setData((_data: any) => {
            const __data = { ..._data };

            if (__data.formFieldKeys && __data.formFieldKeys.length) {
                const _formFieldKey = __data.formFieldKeys.find((ffk: IFormFieldKeyManage) => ffk.id === ffkId);

                if (!_formFieldKey) {
                    return __data as IFormTemplateSectionFormManage;
                }

                if (_formFieldKey.formField) {
                    _formFieldKey.formField.fieldValue = value;
                } else {
                    _formFieldKey.formField = {
                        id: 0,
                        fieldValue: value,
                        formFieldKeyId: _formFieldKey.id,
                        formTemplateSectionId: _formFieldKey.formTemplateSectionId,
                        formSectionId: params.formSectionId ?? 0
                    };
                }
            }

            return { ...__data } as IFormTemplateSectionFormManage;
        });
    }

    function submit(event: any) {
        event.preventDefault();

        let _formFields: Array<any> = [];
        if (data && data.formFieldKeys && data.formFieldKeys.length) {
            _formFields = data.formFieldKeys.map(ffk => {
                const _formField = ffk.formField || {
                    id: 0,
                    fieldValue: "",
                    fieldValueTest: "",
                    formFieldKeyId: ffk.id,
                    formTemplateSectionId: ffk.formTemplateSectionId,
                    formSectionId: params.formSectionId ?? 0
                };

                if (ffk.formFieldValueTypeId === FormFieldValueTypeEnum.Signature) {
                    const sig = signatureRefs.current[ffk.id];
                    _formField.fieldValue =
                        sig && !sig.isEmpty()
                            ? sig.toDataURL("image/png")
                            : (ffk.formField?.fieldValue ?? "");
                }

                return _formField;
            });
        }

        const form = {
            id: params.formSectionId ?? 0,
            formId: _formId ?? 0,
            formTemplateSectionId: params.formTemplateSectionId ?? 0,
            formSectionId: params.formSectionId ?? 0,
            formFields: _formFields
        };

        const formData = new FormData();
        formData.append(`id`, form.id.toString());
        formData.append(`formId`, form.formId.toString());
        formData.append(`formTemplateSectionId`, form.formTemplateSectionId.toString());
        formData.append(`formSectionId`, form.formSectionId.toString());

        form.formFields.forEach((formField, index) => {
            for (const formFieldName in formField) {
                const val = (formField as any)[formFieldName];
                formData.append(`formFields[${index}].${formFieldName}`, val || (val === null || val === "" || val === " " ? "" : 0));
            }
        });

        const documentUploadParentFields = document.getElementsByClassName("documentUpload");
        const documentsToClear: Array<string> = [];

        if (documentUploadParentFields && documentUploadParentFields.length > 0) {
            let currentIndex = 0;
            Array.from(documentUploadParentFields).forEach((documentUploadParentField: any) => {
                const fileField = documentUploadParentField.getElementsByClassName("MuiInputBase-input")[0];
                documentsToClear.push(fileField.id);

                const id = fileField.id.split("_")[1];

                if (data && data.formFieldKeys && data.formFieldKeys.length && (fileField as any)?.files[0]) {
                    formData.append("file[" + currentIndex + "].file", (fileField as any)?.files[0]);
                    formData.append("file[" + currentIndex + "].formFieldKeyId", id);
                    currentIndex++;
                }
            });
        }

        setIsSaving(true);
        formSectionService[params.actionId ? "updateOnFormActionSave" : "postFromData"](formData)
            .then(
                () => {
                    documentsToClear.forEach(d => {
                        const doc = document.getElementById(d);
                        (doc as any).value = null;
                    });

                    params.onSave();
                },
                handleErrorResponseAxios
            )
            .finally(() => {
                setIsSaving(false);
            });
    }

    function valuetext(value: number) {
        return `�${value.toLocaleString()}`;
    }

    function getSliderMarks(minValue?: number, maxValue?: number) {
        minValue = minValue || 0;
        maxValue = maxValue || 9999;
        const marks: any[] = [];

        const val = Math.ceil(maxValue / 4);
        for (let i = 0; i < 5; i++) {
            marks.push({
                value: val * i,
                label: valuetext(val * i),
            });
        }

        return marks;
    }

    function getField(field: IFormFieldKeyManage, index: number) {
        const validators: string[] = [];
        const validatorMessages: string[] = [];
        const isDisabled = params.isForReview || field.isDisabledByDefault;
        const variant = "outlined";

        if (field.isRequired) {
            validators.push("required");
            validatorMessages.push("This field is required");
        }

        switch (field.formFieldValueTypeId) {
            case FormFieldValueTypeEnum.String:
                return (
                    <StandardTextValidator
                        label={field.label}
                        value={field.formField?.fieldValue ?? ""}
                        name={field.shortName}
                        required={field.isRequired}
                        helperText={field.description}
                        fullWidth
                        disabled={isDisabled}
                        type={"text"}
                        variant={variant}
                        validators={[...validators]}
                        errorMessages={[...validatorMessages]}
                        onChange={(e: any) => { handleChange(e, index); }}
                    />
                );

            case FormFieldValueTypeEnum.Date:
                return (
                    <StandardTextValidator
                        label={field.label}
                        value={field.formField?.fieldValue ?? ""}
                        name={field.shortName}
                        required={field.isRequired}
                        fullWidth
                        helperText={field.description}
                        type={"date"}
                        disabled={isDisabled}
                        variant={variant}
                        validators={[...validators]}
                        errorMessages={[...validatorMessages]}
                        onChange={(e: any) => { handleChange(e, index); }}
                    />
                );

            case FormFieldValueTypeEnum.Email:
                return (
                    <StandardTextValidator
                        label={field.label}
                        value={field.formField?.fieldValue ?? ""}
                        name={field.shortName}
                        required={field.isRequired}
                        fullWidth
                        helperText={field.description}
                        type={"email"}
                        disabled={isDisabled}
                        variant={variant}
                        validators={[...validators, "isEmail"]}
                        errorMessages={[...validatorMessages, "Email is not valid"]}
                        onChange={(e: any) => { handleChange(e, index); }}
                    />
                );

            case FormFieldValueTypeEnum.PhoneNumber:
                return (
                    <StandardTextValidator
                        label={field.label}
                        value={field.formField?.fieldValue ?? ""}
                        name={field.shortName}
                        required={field.isRequired}
                        variant={variant}
                        fullWidth
                        helperText={field.description}
                        type={"text"}
                        disabled={isDisabled}
                        validators={field.isRequired ? ["required"] : []}
                        errorMessages={field.isRequired ? ["This field is required"] : []}
                        onChange={(e: any) => { handleChange(e, index); }}
                    />
                );

            case FormFieldValueTypeEnum.Dropdown:
                return (
                    <FormControl fullWidth disabled={isDisabled}>
                        <Grid container>
                            <Grid item xs={12}>
                                <Autocomplete
                                    disabled={isDisabled}
                                    key={index}
                                    options={field.formFieldKeyLookups!}
                                    getOptionLabel={(option) => option.lookupValue}
                                    isOptionEqualToValue={(option, value) => option.lookupValue === value.lookupValue}
                                    onChange={(event, newValue) => {
                                        handleAutocompleteChange(event, index, newValue?.lookupValue);
                                    }}
                                    value={
                                        field.formFieldKeyLookups?.find(
                                            (opt) => opt.lookupValue === field.formField?.fieldValue
                                        ) ?? null
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={field.label}
                                            required={field.isRequired}
                                            variant={"outlined"}
                                            fullWidth
                                            error={!field.formField?.fieldValue && isSaving === true}
                                            helperText={
                                                field.description ? (
                                                    <Alert severity="info" sx={{ mt: 1 }}>
                                                        {field.description}
                                                    </Alert>
                                                ) : (
                                                    " "
                                                )
                                            }
                                        />
                                    )}
                                />
                            </Grid>

                            <StandardTextValidator
                                disabled={isDisabled}
                                sx={{ ".MuiInputBase-root": { display: "none" } }}
                                value={field.formField?.fieldValue}
                                name={field.shortName}
                                validators={["isAutocompleteValid"]}
                                errorMessages={[`This field is required`]}
                            />
                        </Grid>
                    </FormControl>
                );

            case FormFieldValueTypeEnum.Radio:
                return (
                    <FormControl disabled={isDisabled}>
                        <FormLabel id={field.label}>{field.label}</FormLabel>
                        <RadioGroup
                            row
                            aria-labelledby={field.label}
                            onChange={(e: any) => { handleChange(e, index); }}
                            name={field.shortName}
                        >
                            {field.formFieldKeyLookups?.map(lookup => (
                                <FormControlLabel
                                    key={"list_item_radio_" + field.id + "_" + lookup.id}
                                    required={field.isRequired}
                                    checked={!!field.formField?.fieldValue && safeLower(lookup.lookupValue) === safeLower(field.formField?.fieldValue)}
                                    value={lookup.lookupValue}
                                    label={lookup.label}
                                    control={
                                        <Tooltip title={lookup.description}>
                                            <Radio disabled={isDisabled} key={"list_item_radio_sub_" + field.id + "_" + lookup.id} />
                                        </Tooltip>
                                    }
                                />
                            ))}
                        </RadioGroup>
                    </FormControl>
                );

            case FormFieldValueTypeEnum.Text:
                return <Typography color={isDisabled ? "text.disabled" : "primary"} variant={field.textFontSize} textAlign="center">{field.label}</Typography>;

            case FormFieldValueTypeEnum.Textarea:
                return (
                    <StandardTextValidator
                        label={field.label}
                        value={field.formField?.fieldValue ?? ""}
                        name={field.shortName}
                        required={field.isRequired}
                        fullWidth
                        multiline
                        helperText={field.description}
                        type={"text"}
                        rows={4}
                        disabled={isDisabled}
                        variant={variant}
                        validators={field.isRequired ? ["required"] : []}
                        errorMessages={field.isRequired ? ["This field is required"] : []}
                        onChange={(e: any) => { handleChange(e, index); }}
                    />
                );

            case FormFieldValueTypeEnum.Number:
                return (
                    <StandardTextValidator
                        label={field.label}
                        value={field.formField?.fieldValue ?? ""}
                        name={field.shortName}
                        required={field.isRequired}
                        fullWidth
                        helperText={field.description}
                        type={"number"}
                        disabled={isDisabled}
                        variant={"outlined"}
                        validators={field.isRequired ? ["required"] : []}
                        errorMessages={field.isRequired ? ["This field is required"] : []}
                        onChange={(e: any) => { handleChange(e, index); }}
                    />
                );

            case FormFieldValueTypeEnum.Signature:
                return !params.isForReview ? (
                    <div>
                        <Typography sx={{ mb: 1 }}>
                            {field.label || "Signature"} {field.isRequired ? "*" : ""}
                        </Typography>

                        <Box
                            sx={{
                                position: "relative",
                                background: "#fff",
                                border: "1px solid #ccc",
                                width: "100%",
                                maxWidth: "800px",
                            }}
                        >
                            <IconButton
                                size="small"
                                aria-label="Clear signature"
                                onClick={() => {
                                    signatureRefs.current[field.id]?.clear();
                                    handleChange({ target: { value: "" } }, index);
                                }}
                                sx={{
                                    position: "absolute",
                                    top: 8,
                                    right: 8,
                                    zIndex: 2,
                                    backgroundColor: "rgba(255,255,255,0.9)",
                                    border: "1px solid #ddd",
                                    "&:hover": {
                                        backgroundColor: "rgba(255,255,255,1)",
                                    },
                                }}
                            >
                                <Clear fontSize="small" color="warning" />
                            </IconButton>

                            <SignatureCanvas
                                penColor="black"
                                ref={getSignatureRef(field.id)}
                                onEnd={() => {
                                    const sig = signatureRefs.current[field.id];
                                    handleChange(
                                        { target: { value: sig && !sig.isEmpty() ? sig.toDataURL("image/png") : "" } },
                                        index
                                    );
                                }}
                                canvasProps={{
                                    width: 800,
                                    height: 200,
                                    className: "sigCanvas",
                                    style: {
                                        width: "100%",
                                        maxWidth: "800px",
                                        height: "200px",
                                        display: "block",
                                        background: "#fff",
                                    },
                                }}
                            />
                        </Box>
                    </div>
                ) : field.formField?.fieldValue ? (
                    <img
                        src={field.formField.fieldValue}
                        alt={field.label || "Signature"}
                        style={{
                            width: "100%",
                            maxWidth: 800,
                            maxHeight: 200,
                            objectFit: "contain",
                            border: "1px solid silver",
                            display: "block",
                            background: "#fff",
                        }}
                    />
                ) : (
                    <Typography color="text.secondary">No signature provided</Typography>
                );

            case FormFieldValueTypeEnum.FileUpload:
                return (
                    <Grid item xs={12}>
                        {!params.isForReview && (
                            <TextField
                                className={"documentUpload"}
                                id={"documentUpload_" + field.id}
                                label={field.label}
                                defaultValue={""}
                                name={field.shortName}
                                required={field.isRequired && !field.formField?.id}
                                type={"file"}
                                disabled={isDisabled}
                                error={field.isRequired && !field.formField?.id && !field.formField?.fieldValue}
                                helperText={field.isRequired && !field.formField?.id && !field.formField?.fieldValue ? "This field is required" : ""}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                onChange={(e: any) => { handleChange(e, index); }}
                            />
                        )}

                        {!field.formField?.id && (
                            <StandardTextValidator
                                label={field.label}
                                value={field.formField?.fieldValue ?? ""}
                                name={field.shortName}
                                required={field.isRequired}
                                hidden
                                type={"text"}
                                disabled={isDisabled}
                                variant={variant}
                                validators={field.isRequired ? ["required"] : []}
                                errorMessages={field.isRequired ? ["This field is required"] : []}
                                onChange={(e: any) => { handleChange(e, index); }}
                            />
                        )}

                        {params.isForReview && (
                            <PrimaryButton icon={"download"} variant="primary" type="button" onClick={() => {
                                tokenService.GetReportToken().then((data) => {
                                    return window.open(API_URL + "/formField/GetUploadById/?token=" + data + "&fileId=" + field.formField?.id, "_blank");
                                });
                            }}>{field.shortName || field.label}</PrimaryButton>
                        )}
                    </Grid>
                );

            case FormFieldValueTypeEnum.ImageUpload:
                return (
                    <Grid item xs={12}>
                        {!params.isForReview && (
                            <TextField
                                className={"documentUpload"}
                                id={"documentUpload_" + field.id}
                                label={field.label}
                                defaultValue={""}
                                name={field.shortName}
                                required={field.isRequired && !field.formField?.id}
                                type={"file"}
                                disabled={isDisabled}
                                error={field.isRequired && !field.formField?.id && !field.formField?.fieldValue}
                                helperText={field.isRequired && !field.formField?.id && !field.formField?.fieldValue ? "This field is required" : ""}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                onChange={(e: any) => { handleChange(e, index); }}
                            />
                        )}

                        {!field.formField?.id && (
                            <StandardTextValidator
                                label={field.label}
                                value={field.formField?.fieldValue ?? ""}
                                name={field.shortName}
                                required={field.isRequired}
                                hidden
                                type={"text"}
                                disabled={isDisabled}
                                variant={variant}
                                validators={field.isRequired ? ["required"] : []}
                                errorMessages={field.isRequired ? ["This field is required"] : []}
                                onChange={(e: any) => { handleChange(e, index); }}
                            />
                        )}

                        {params.isForReview && (
                            <PrimaryButton icon={"download"} variant="primary" type="button" onClick={() => {
                                tokenService.GetReportToken().then((data) => {
                                    return window.open(API_URL + "/formField/GetUploadById/?token=" + data + "&fileId=" + field.formField?.id, "_blank");
                                });
                            }}>{field.shortName || field.label}</PrimaryButton>
                        )}
                    </Grid>
                );

            case FormFieldValueTypeEnum.List:
                return (
                    <>
                        {field.showLabel && (
                            <Typography>
                                {field.label}
                            </Typography>
                        )}

                        <List sx={{ listStyle: "decimal", pl: 4 }}>
                            {field.formFieldKeyLookups?.map(lookup => (
                                <ListItem key={"list_item_list_" + lookup.id} sx={{ display: "list-item" }}>
                                    <ListItemText primary={lookup.label || lookup.lookupValue} />
                                </ListItem>
                            ))}
                        </List>
                    </>
                );

            case FormFieldValueTypeEnum.Title:
                return <Typography color={params.isForReview ? "text.disabled" : "primary"} variant="h5" textAlign="center">{field.label}</Typography>;

            case FormFieldValueTypeEnum.Image:
                return (
                    <img
                        style={{
                            width: "100%",
                            marginTop: "10px",
                            display: "block",
                            marginLeft: "auto",
                            marginRight: "auto",
                            marginBottom: "10px"
                        }}
                        src={"data:image/jpeg;charset=utf-8;base64," + field.label}
                        alt={"test"}
                    />
                );

            case FormFieldValueTypeEnum.Checkbox:
                return (
                    <FormControlLabel
                        disabled={isLoading || params.isForReview}
                        value={field.formField?.fieldValue}
                        required={field.isRequired}
                        style={{ paddingLeft: "16px" }}
                        onChange={(e: any) => { handleChange(e, index); }}
                        checked={safeLower(field.formField?.fieldValue) === "yes"}
                        control={<Checkbox style={{ paddingRight: "8px" }} value="Yes" color="primary" />}
                        label={field.label}
                    />
                );

            case FormFieldValueTypeEnum.Consent:
                return (
                    <FormControlLabel
                        disabled={isLoading || params.isForReview}
                        value={field.formField?.fieldValue}
                        required={field.isRequired}
                        style={{ paddingLeft: "16px" }}
                        onChange={(e: any) => { handleChange(e, index); }}
                        checked={safeLower(field.formField?.fieldValue) === "yes"}
                        control={<Checkbox style={{ paddingRight: "8px" }} value="Yes" color="primary" />}
                        label={
                            <span
                                dangerouslySetInnerHTML={{ __html: sanitizeFormLabelHtml(String(field.label ?? "")) }}
                                onClick={(e) => {
                                    if ((e.target as HTMLElement | null)?.closest?.("a")) {
                                        e.stopPropagation();
                                    }
                                }}
                            />
                        }
                    />
                );

            case FormFieldValueTypeEnum.FileDownload:
                return (
                    <PrimaryButton disabled={params.isForReview} icon={"download"} variant="primary" type="button" onClick={() => {
                        tokenService.GetReportToken().then((data) => {
                            return window.open(API_URL + "/formField/GetDownloadById/?fileId=" + field.label + "&token=" + data, "_blank");
                        });
                    }}>{field.shortName || field.label}</PrimaryButton>
                );

            case FormFieldValueTypeEnum.RadioButton:
                return (
                    <FormControl fullWidth>
                        {field.showLabel && <FormLabel id={field.label}>{field.label} {field.isRequired ? "*" : ""}</FormLabel>}

                        <Grid container spacing={1}>
                            {field.formFieldKeyLookups?.map(lookup => (
                                <Grid
                                    key={"radio_btn_" + field.id + "_" + lookup.id}
                                    item
                                    xs={(field.formFieldKeyLookups?.length || 0) % 8 == 0 ? 3 : (field.formFieldKeyLookups?.length || 0) % 7 == 0 ? 3 : (field.formFieldKeyLookups?.length || 0) % 6 == 0 ? 4 : (field.formFieldKeyLookups?.length || 0) % 5 == 0 ? 4 : (field.formFieldKeyLookups?.length || 0) % 4 == 0 ? 3 : (field.formFieldKeyLookups?.length || 0) % 3 == 0 ? 4 : (field.formFieldKeyLookups?.length || 0) % 2 == 0 ? 6 : 12}
                                >
                                    <ToggleButton
                                        fullWidth
                                        color="primary"
                                        onChange={(e: any) => { handleChange(e, index); }}
                                        selected={!!field.formField?.fieldValue && safeLower(field.formField?.fieldValue) === safeLower(lookup.lookupValue)}
                                        value={lookup.lookupValue}
                                        aria-label="left aligned"
                                    >
                                        {lookup.label || lookup.lookupValue}
                                    </ToggleButton>
                                </Grid>
                            ))}
                        </Grid>
                    </FormControl>
                );

            case FormFieldValueTypeEnum.Slider:
                return (
                    <FormControl fullWidth>
                        <FormLabel id={field.label}>{field.label}: {valuetext(Number(field.formField?.fieldValue || 0))}</FormLabel>
                        <Grid container spacing={6}>
                            <Grid item xs={6}>
                                <Slider
                                    aria-label="Custom marks"
                                    defaultValue={field.minValue || 0}
                                    value={Number(field.formField?.fieldValue || "")}
                                    onChange={(e: any) => { handleChange(e, index); }}
                                    getAriaValueText={valuetext}
                                    step={1000}
                                    min={field.minValue}
                                    max={field.maxValue}
                                    valueLabelDisplay="off"
                                    name={field.shortName}
                                    marks={getSliderMarks(field.minValue, field.maxValue)}
                                />
                            </Grid>
                            <Grid item xs={2}>
                                <StandardTextValidator
                                    value={field.formField?.fieldValue ?? "0"}
                                    name={field.shortName}
                                    required={field.isRequired}
                                    fullWidth
                                    type={"number"}
                                    disabled={isDisabled}
                                    variant={variant}
                                    validators={field.isRequired ? ["required"] : []}
                                    errorMessages={field.isRequired ? ["This field is required"] : []}
                                    onChange={(e: any) => { handleChange(e, index); }}
                                />
                            </Grid>
                            <Grid item xs={2}></Grid>
                        </Grid>
                    </FormControl>
                );

            case FormFieldValueTypeEnum.DataTypeAutoComplete:
                return (
                    <DataTypeAutoComplete
                        dataType={field.dataType}
                        label={field.label}
                        valueField="name"
                        value={field.formField?.fieldValue ?? ""}
                        name={field.shortName}
                        required={field.isRequired}
                        disabled={isDisabled}
                        onChange={(e: any) => {
                            handleChangeDataType(e, index);
                        }}
                    />
                );

            case FormFieldValueTypeEnum.WarningAlert:
                return <Alert severity="warning">{field.label}</Alert>;

            case FormFieldValueTypeEnum.InfoAlert:
                return <Alert severity="info">{field.label}</Alert>;

            case FormFieldValueTypeEnum.SuccessAlert:
                return <Alert severity="success">{field.label}</Alert>;

            case FormFieldValueTypeEnum.DangerAlert:
                return <Alert severity="error">{field.label}</Alert>;

            default:
                return ";";
        }
    }

    function isFormValid() {
        let isFormValid = true;

        getSections().filter(s => checkIfDependancyMet(s)).forEach((s) => {
            getSectionKeys(s.id)?.filter((m: any) => checkIfDependancyMet(m)).filter((m: any) => m.isRequired).forEach(ffk => {
                if (ffk.isRequired && (ffk.formField?.fieldValue === null || ffk.formField?.fieldValue === undefined || ffk.formField?.fieldValue === "")) {
                    console.log("[FormSectionOriginalManage] isFormValid=false", {
                        reason: "Required visible field has empty formField.fieldValue",
                        fieldId: ffk.id,
                        shortName: ffk.shortName,
                        label: ffk.label,
                        value: ffk.formField?.fieldValue
                    });
                    isFormValid = false;
                }
            });
        });

        if (isFormValid) {
            console.log("[FormSectionOriginalManage] isFormValid=true");
        }

        return isFormValid;
    }

    function checkIfLookupDependancyMet(ffkfl: IFormFieldKeyLookupManage) {
        if (!ffkfl.conditionFormFieldKeyId) return true;

        const globalFormField = data?.globalFormFields?.find(m => m.formFieldKeyId == ffkfl.conditionFormFieldKeyId);

        return (
            safeLower(globalFormField?.fieldValue) !== "" &&
            safeLower(globalFormField?.fieldValue) === safeLower(ffkfl.conditionFormFieldKeyValue)
        );
    }

    function checkIfDependancyMet(formFieldKey: IFormFieldKeyManage) {
        if (formFieldKey.isAdminField && !accountService.isAdmin) {
            return false;
        }

        if (
            (!formFieldKey.formFieldKeyFormFieldKeyLookups || !formFieldKey.formFieldKeyFormFieldKeyLookups.length) &&
            (!formFieldKey.formFieldKeyConditionDependencies || !formFieldKey.formFieldKeyConditionDependencies.length)
        ) {
            return true;
        }

        let _return: boolean | null = null;

        if (formFieldKey.formFieldKeyFormFieldKeyLookups && formFieldKey.formFieldKeyFormFieldKeyLookups.length) {
            formFieldKey.formFieldKeyFormFieldKeyLookups.forEach(ffkfl => {
                if (data?.formFieldKeys) {
                    data.formFieldKeys.filter(ffk => ffk.formFieldKeyLookups).forEach(ffk => {
                        if (ffk.formFieldKeyLookups?.some(ffkl => ffkl.id == ffkfl.formFieldKeyLookupId)) {
                            if (ffk && ffk.formField && ffk.formField.fieldValue == ffkfl.value) _return = true;
                        }
                    });

                    const globalFormField = data?.globalFormFields?.find(m => m.formFieldKeyId == ffkfl.formFieldKeyLookupId);
                    if (
                        safeLower(globalFormField?.fieldValue) !== "" &&
                        safeLower(globalFormField?.fieldValue) === safeLower(ffkfl.value)
                    ) {
                        _return = true;
                    }
                }
            });
        }

        if (formFieldKey.formFieldKeyConditionDependencies && formFieldKey.formFieldKeyConditionDependencies.length) {
            formFieldKey.formFieldKeyConditionDependencies.forEach((ffkcd: any) => {
                const globalFormField = data?.globalFormFields?.find(m => m.formFieldKeyId == ffkcd.conditionFormFieldKeyId);

                if (!globalFormField) {
                    _return = false;
                    return;
                }

                if (
                    _return === null ||
                    (_return === true && formFieldKey.formFieldKeyConditionTypeId === FormFieldKeyConditionTypeEnum.All) ||
                    (_return !== true && formFieldKey.formFieldKeyConditionTypeId === FormFieldKeyConditionTypeEnum.Any)
                ) {
                    if (ffkcd.formFieldKeyConditionId === FormFieldKeyConditionEnum.EqualsTo) {
                        _return = safeLower(globalFormField.fieldValue) === safeLower(ffkcd.value);
                    } else if (ffkcd.formFieldKeyConditionId === FormFieldKeyConditionEnum.EqualsOrGreater) {
                        _return = Number(globalFormField.fieldValue) >= Number(ffkcd.value);
                    } else if (ffkcd.formFieldKeyConditionId === FormFieldKeyConditionEnum.EqualsOrLess) {
                        _return = Number(globalFormField.fieldValue) <= Number(ffkcd.value);
                    } else if (ffkcd.formFieldKeyConditionId === FormFieldKeyConditionEnum.Less) {
                        _return = Number(globalFormField.fieldValue) < Number(ffkcd.value);
                    } else if (ffkcd.formFieldKeyConditionId === FormFieldKeyConditionEnum.Greater) {
                        _return = Number(globalFormField.fieldValue) > Number(ffkcd.value);
                    } else if (ffkcd.formFieldKeyConditionId === FormFieldKeyConditionEnum.Contains) {
                        _return = safeString(globalFormField.fieldValue).trim().toLowerCase().includes(safeString(ffkcd.value).trim().toLowerCase());
                    }
                }
            });
        }

        if (_return === null) _return = false;

        return _return;
    }

    function getSections() {
        let subSections: any[] = [];

        if (data && data.formFieldKeys && data.formFieldKeys) {
            subSections = data.formFieldKeys.filter(ffk => ffk.formFieldValueTypeId === FormFieldValueTypeEnum.SubSection);
            if (data.formFieldKeys.some(ffk => ffk.formFieldValueTypeId !== FormFieldValueTypeEnum.SubSection && !ffk.subSectionId)) {
                subSections.push({});
            }
        }

        return subSections || [{}];
    }

    function getSectionKeys(subSectionId?: any) {
        if (data && data.formFieldKeys && data.formFieldKeys) {
            return data.formFieldKeys.filter(ffk => ffk.formFieldValueTypeId !== FormFieldValueTypeEnum.SubSection && ffk.subSectionId == subSectionId);
        }

        return [];
    }

    function withdraw() {
        if (window.confirm("Are you sure you want to withdraw this form?") == true) {
            setIsSaving(true);
            formService.disable(_formId, FormStatusEnum.Withdrawn).then(
                () => { navigate("../"); },
                handleErrorResponse
            ).finally(() => {
                setIsSaving(false);
            });
        }
    }

    return (
        <Grid container spacing={1} id="form-manage" key={"form_template_section_" + params.formTemplateSectionId + "_"}>
            <Grid item xs={12}>
                {(isLoading || isSaving || params.isSectionSaving) && (
                    <Paper>
                        <Typography variant="body1" className="paper-theme-body" gutterBottom>
                            <LinearProgress color="secondary" />
                        </Typography>
                    </Paper>
                )}

                <ValidatorForm onSubmit={submit} noValidate>
                    {getSections().filter(s => checkIfDependancyMet(s)).map((s, index) => (
                        <Paper key={"section_" + s.id + "_" + index + "_"} sx={{ marginBottom: "8px", marginTop: "32px !important", paddingTop: "11px" }}>
                            <Grid container spacing={2}>
                                {getSectionKeys(s.id).filter(s => checkIfDependancyMet(s)).map((ffk, index) => (
                                    <Grid key={"subsection" + s.id + "_" + index + "_" + ffk.id} item xs={ffk.xsWidth || 12} md={ffk.mdWidth || 12}>
                                        {getField(ffk, ffk.id)}
                                    </Grid>
                                ))}
                            </Grid>
                        </Paper>
                    ))}

                    {!params.isForReview && (
                        <Grid container spacing={1} style={{ padding: "0px" }}>
                            <Grid item xs={12}>
                                <PrimaryButton disabled={!isFormValid()} icon={"check"} isSaving={isLoading || isSaving || params.isSectionSaving} variant="success" type="submit">
                                    {params.isLastSection ? "Submit" : "Continue"}
                                </PrimaryButton>
                            </Grid>
                        </Grid>
                    )}
                </ValidatorForm>
            </Grid>
        </Grid>
    );
}